// Explore-stats page: Committee statistics.
// Recreates the Handbook page — committee types by Parliament, members by
// type, longest-running & largest committees, and a word cloud of names.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Grid } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatStackedBar, StatBar, PALETTE } from './charts';

function WordCloud({ words }) {
  if (!words || !words.length) return null;
  const max = words[0].value || 1;
  const min = words[words.length - 1].value || 1;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
               justifyContent: 'center', p: 1 }}>
      {words.map((w, i) => {
        const t = max === min ? 0.5 : (w.value - min) / (max - min);
        return (
          <Box key={w.text} component="span"
               title={`${w.text}: ${w.value}`}
               sx={{ fontSize: `${0.8 + t * 1.9}rem`, lineHeight: 1.1,
                     color: PALETTE[i % PALETTE.length], fontWeight: 600 }}>
            {w.text}
          </Box>
        );
      })}
    </Box>
  );
}

export default function CommitteeStatistics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('committee-statistics');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Committee statistics"
      description={`Parliamentary committees${
        c.total ? ` (${c.total})` : ''
      } — the expanding role of committees over time, the longest-running and largest committees, and the themes in their names.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="committee-statistics"
    >
      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Committee types by Parliament</Typography>
            <StatStackedBar data={c.types_by_parliament || []} xKey="parliament"
                            keys={c.type_series || []} height={380} />
          </Paper>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Total committee members by type</Typography>
            <StatStackedBar data={c.members_by_type || []} xKey="parliament"
                            keys={c.type_series || []} height={380} />
          </Paper>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Longest running committees</Typography>
                <StatBar data={c.longest_running || []} xKey="name" yKey="years"
                         layout="vertical" height={360} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Largest committees by total members</Typography>
                <StatBar data={c.largest || []} xKey="name" yKey="members"
                         layout="vertical" height={360} />
              </Paper>
            </Grid>
          </Grid>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Word cloud derived from committee names</Typography>
            <WordCloud words={c.word_cloud || []} />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
